/**
 * general utils that can be used in all projects
 */

import uniqBy from 'lodash/uniqBy';

/**
 * remove duplicate objects from an array
 */
export const uniques = <T extends { id: string | number }>(array: T[]): T[] => {
  return uniqBy(array, (e: T) => e.id);
};

/**
 * builds a function to check whether an id is in the list of ids
 */
export const isInIds = (ids: string[]): ((id: string) => boolean) => {
  const set = new Set(ids);
  return (id: string): boolean => {
    return set.has(id);
  };
};

/**
 * builds a function to check whether the id of an object is in the list of ids
 */
export const isObjectInIds = (
  ids: string[]
): (<T extends { id: string }>(object: T) => boolean) => {
  const set = new Set(ids);
  return <T extends { id: string }>(object: T): boolean => {
    return set.has(object.id);
  };
};

export const getStdOf = <T>(
  array: T[],
  valueGetter: (item: T) => number
): number => {
  let sum = 0;

  for (const item of array) {
    const value = valueGetter(item);
    sum += value;
  }

  const mean = sum / array.length;
  const deviationSquareSum = array.reduce((sum: number, item: T) => {
    return sum + Math.pow(valueGetter(item) - mean, 2);
  }, 0);

  return Math.sqrt(deviationSquareSum / array.length);
};

/**
 * Converts a Blob into a Data URL.
 *
 * @param blob the Blob to convert.
 * @returns a promise that resolves with the Data URL string.
 */
export const blobToDataURL = async (
  blob: Blob
): Promise<string | ArrayBuffer | null> => {
  const arrayBuffer = await blob.arrayBuffer();
  const buffer = Buffer.from(arrayBuffer);
  return `data:${blob.type};base64,${buffer.toString('base64')}`;
};

export type Range = [number, number];

/**
 * Converts a value from a domain range to a target range
 * by linear interpolation.
 * @param value - The value to convert
 * @param rangeFrom - The domain range
 * @param rangeTo - The target range
 */
export const mapToRange = (
  value: number,
  rangeFrom: Range,
  rangeTo: Range
): number => {
  const fromLength = rangeFrom[1] - rangeFrom[0];
  const toLength = rangeTo[1] - rangeTo[0];
  const valueRelative = (value - rangeFrom[0]) / fromLength;
  return rangeTo[0] + valueRelative * toLength;
};

export const pluralSuffix = (amount: number): string =>
  amount === 1 ? '' : 's';

export const getReversedObject = (
  dict: Record<string, string>
): Record<string, string> => {
  return Object.entries(dict).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    {}
  );
};

/**
 * Useful if you don't want to create a new object every time
 * to avoid unnecessary re-renders
 */
export const emptyObject = Object.freeze({});
export const emptyMap = Object.freeze(new Map()) as Readonly<Map<never, never>>;

/**
 * Utility function to remove from the object type this argument
 *
 * Example
 *
 *  class A {
 *    foo(this: A): void {}
 *    boo() {}
 *    someProp = 1;
 *  }
 *
 *  type B = ReturnType<typeof getClassPrototypeAsEnumerable<A>>;
 *
 * B will be {
 *    foo(): void {} <- notice this is missing in the arg
 *    boo() {}
 *    someProp = 1;
 * }
 */
export type RemoveThisFromFunctionArgs<T extends object> = {
  [key in keyof T]: OmitThisParameter<T[key]>;
};

/**
 * This function allow to copy proto function from the class into the object
 * This function ignores enumerable: false flag which allows to copy
 */
export const getClassPrototypeAsEnumerable = <T>(
  proto: T
): RemoveThisFromFunctionArgs<Omit<T, 'constructor'>> => {
  const protoObject = proto as Record<string, unknown>;

  const newProto: Record<string, unknown> = {};

  const keys = Object.getOwnPropertyNames(proto);

  for (const prop of keys) {
    // skip constructor function
    if (prop === 'constructor') {
      continue;
    }

    newProto[prop] = protoObject[prop];
  }

  return newProto as unknown as RemoveThisFromFunctionArgs<
    Omit<T, 'constructor'>
  >;
};
